import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription, SheetHeader } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu, Globe, Check } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { LOCALE_META, SUPPORTED_LOCALES } from "@/i18n/translations";
import TbsLogo from "@/components/TbsLogo";

// type: 'anchor' (home-page section) | 'route' (separate page)
const NAV_LINKS = [
  { id: "home", type: "anchor", href: "#home" },
  { id: "about", type: "anchor", href: "#about" },
  { id: "team", type: "route", href: "/ekibimiz" },
  { id: "export", type: "route", href: "/ihracat" },
  { id: "technology", type: "route", href: "/teknoloji" },
  { id: "construction", type: "route", href: "/insaat" },
  { id: "hr", type: "route", href: "/insan-kaynaklari" },
  { id: "references", type: "anchor", href: "#references" },
  { id: "contact", type: "anchor", href: "#contact" },
];

const LanguageSelector = ({ inSheet = false }) => {
  const { locale, setLocale, t } = useI18n();
  const current = LOCALE_META[locale] || LOCALE_META.en;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t("common.language", "Language")}
          data-testid="navbar-language-toggle"
          className={`inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 h-10 text-xs font-semibold text-slate-700 hover:border-[rgba(201,162,39,0.55)] hover:text-slate-900 ${inSheet ? "" : ""}`}
        >
          <Globe className="h-3.5 w-3.5 text-[hsl(var(--accent))]" />
          <span className="tabular-nums">{current.code}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 max-h-[70vh] overflow-y-auto">
        {SUPPORTED_LOCALES.map((code) => {
          const meta = LOCALE_META[code];
          const active = code === locale;
          return (
            <DropdownMenuItem
              key={code}
              onClick={() => setLocale(code)}
              data-testid={`navbar-language-option-${code}`}
              className={`cursor-pointer flex items-center justify-between ${active ? "bg-slate-50" : ""}`}
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-6 w-8 rounded-md bg-slate-100 text-[10px] font-bold tracking-wider text-slate-700">
                  {meta.code}
                </span>
                <span className="text-sm font-medium text-slate-800">{meta.native}</span>
              </div>
              {active && <Check className="h-4 w-4 text-[hsl(var(--accent))]" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Navbar = () => {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (e, link) => {
    e.preventDefault();
    setOpen(false);
    if (link.type === "route") {
      navigate(link.href);
      window.scrollTo({ top: 0, behavior: "auto" });
      return;
    }
    // anchor
    const id = link.href.replace("#", "");
    if (location.pathname !== "/") {
      navigate("/" + link.href);
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState(null, "", link.href);
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const handleContactCta = (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/#contact");
      return;
    }
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="navbar"
      className={`sticky top-0 z-50 w-full bg-[hsl(var(--background))]/95 backdrop-blur supports-[backdrop-filter]:bg-[hsl(var(--background))]/80 transition-shadow ${
        scrolled ? "shadow-[0_6px_24px_rgba(15,23,42,0.06)] border-b border-slate-200/80" : "border-b border-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-20 md:h-24 flex items-center justify-between gap-4">
        <a
          href="/"
          onClick={handleLogoClick}
          data-testid="navbar-logo-link"
          className="flex items-center gap-2 shrink-0"
        >
          <TbsLogo className="h-16 md:h-20 w-auto" />
        </a>

        <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
          {NAV_LINKS.map((l) => {
            const active =
              l.type === "route" && location.pathname.startsWith(l.href);
            return (
              <a
                key={l.id}
                href={l.href}
                onClick={(e) => handleNavClick(e, l)}
                className="nav-link text-sm font-medium whitespace-nowrap"
                data-active={active ? "true" : "false"}
                data-testid={`navbar-link-${l.id}`}
              >
                {t(`nav.${l.id}`)}
              </a>
            );
          })}
        </nav>

        <div className="flex items-center gap-2.5">
          <div className="hidden sm:block">
            <LanguageSelector />
          </div>
          <Button
            asChild
            size="sm"
            data-testid="navbar-contact-cta"
            className="hidden md:inline-flex bg-[hsl(var(--primary))] hover:bg-[hsl(var(--tbs-navy-800))] text-white rounded-xl h-10 px-4"
          >
            <a href="#contact" onClick={handleContactCta}>
              {t("nav.cta")}
            </a>
          </Button>

          {/* Mobile */}
          <div className="lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  aria-label="Open menu"
                  data-testid="navbar-mobile-trigger"
                  className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-slate-200 bg-white"
                >
                  <Menu className="h-5 w-5 text-slate-800" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[88vw] sm:w-[380px] bg-white">
                <SheetHeader className="text-left">
                  <SheetTitle>
                    <TbsLogo className="h-14 w-auto" />
                  </SheetTitle>
                  <SheetDescription className="sr-only">Site navigation</SheetDescription>
                </SheetHeader>
                <div className="mt-6 flex flex-col gap-1">
                  {NAV_LINKS.map((l) => (
                    <a
                      key={l.id}
                      href={l.href}
                      onClick={(e) => handleNavClick(e, l)}
                      data-testid={`navbar-mobile-link-${l.id}`}
                      className="block rounded-lg px-3 py-3 text-base font-medium text-slate-800 hover:bg-slate-100"
                    >
                      {t(`nav.${l.id}`)}
                    </a>
                  ))}
                </div>
                <div className="mt-6 flex items-center justify-between">
                  <LanguageSelector inSheet />
                  <Button
                    asChild
                    className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--tbs-navy-800))] text-white rounded-xl"
                  >
                    <a href="#contact" onClick={handleContactCta}>
                      {t("nav.cta")}
                    </a>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
